import { useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiFetchError, apiGet, apiPut } from '../../lib/api'
import { qk, queryClient } from '../../lib/queryClient'
import type { UserPrefs } from '../../lib/types'

// Prefs personales: point-read + escrituras con DEBOUNCE (dashboard.md §datos).
// save() aplica el parcial optimista al cache y acumula lo pendiente; a los 600 ms
// se manda UN solo PUT con todo lo acumulado. El merge fino lo hace el server.

const DEBOUNCE_MS = 600

const esFalloDeRed = (e: unknown) =>
  e instanceof ApiFetchError && (e.code === 'sin_red' || e.code === 'red_lenta')

export function usePrefs() {
  const query = useQuery({
    queryKey: qk.prefs,
    queryFn: () => apiGet<{ prefs: UserPrefs }>('/api/prefs').then((r) => r.prefs),
  })

  const pendingRef = useRef<Partial<UserPrefs>>({})
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const body = pendingRef.current
    pendingRef.current = {}
    if (Object.keys(body).length === 0) return
    apiPut<{ prefs: UserPrefs }>('/api/prefs', body)
      .then(({ prefs }) => {
        // Solo pisa el cache si no hay ediciones locales más nuevas en vuelo.
        if (Object.keys(pendingRef.current).length === 0) {
          queryClient.setQueryData(qk.prefs, prefs)
        }
      })
      .catch((err) => {
        // SIN RED es distinto a un error del servidor. Invalidar aquí era el bug:
        // al reconectar, el refetch traía las prefs del servidor SIN el favorito y
        // pisaba el optimista — marcabas la estrella, la veías encendida, y se
        // apagaba sola. Encima qk.prefs se persiste, así que la versión sin el
        // favorito acababa en IndexedDB.
        if (esFalloDeRed(err)) {
          // Se conserva el optimista y lo no enviado vuelve a la cola EN MEMORIA
          // (sin cola en disco: D-15). Lo más nuevo gana si se editó mientras tanto.
          pendingRef.current = { ...body, ...pendingRef.current }
          return
        }
        void queryClient.invalidateQueries({ queryKey: qk.prefs })
      })
  }, [])

  // Al volver la red se manda lo que quedó pendiente. cancelQueries primero: al
  // reconectar, react-query dispara su propio refetch de prefs (refetchOnReconnect)
  // y si ese GET se resuelve después del PUT pero salió antes, traería las prefs
  // previas y volvería a pisar el favorito.
  useEffect(() => {
    const alVolver = () => {
      if (Object.keys(pendingRef.current).length === 0) return
      void queryClient.cancelQueries({ queryKey: qk.prefs })
      flush()
    }
    window.addEventListener('online', alVolver)
    return () => window.removeEventListener('online', alVolver)
  }, [flush])

  const save = useCallback(
    (partial: Partial<UserPrefs>) => {
      queryClient.setQueryData<UserPrefs>(qk.prefs, (old) =>
        old
          ? { ...old, ...partial, notifPrefs: { ...old.notifPrefs, ...(partial.notifPrefs ?? {}) } }
          : old,
      )
      pendingRef.current = { ...pendingRef.current, ...partial }
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(flush, DEBOUNCE_MS)
    },
    [flush],
  )

  // Al desmontar el dashboard, dispara lo pendiente para no perder cambios.
  useEffect(() => () => flush(), [flush])

  // `error` y `recargar` se exponen porque hay pantallas (la pestaña Ajustes de la
  // campanita) donde estas prefs SON el contenido: sin ellas no hay nada que
  // pintar, y quedarse en "Cargando…" para siempre no es un estado honesto.
  return { prefs: query.data, loading: query.isLoading, error: query.error, save, recargar: query.refetch }
}
